// Lib
import ReactDOM from 'react-dom';
import { cond, stubTrue, defer } from 'lodash';
import { NativeTypes } from 'react-dnd-html5-backend';
import * as Immutable from 'immutable';

// Utils
import { isIconViewLike } from '../../../common/elements/utils/elementDisplayUtils';
import { getListChildNewLocation } from '../../../common/elements/utils/elementLocationUtils';
import * as pointLib from '../../../common/maths/geometry/point';
import FileValidationError from '../../../common/error/FileValidationError';

// Constants
import { DEFAULT_ICON_VIEW_WIDTH } from '../../../common/elements/elementDisplayModeConstants';
import { ELEMENT_CREATION_SOURCES, ELEMENT_DND_TYPE } from '../../../common/elements/elementConstants';
import { SHOW_TITLE_PROPERTY } from '../../../common/taskLists/taskListConstants';
import { LINE_CONTROL_POINT_DND_TYPE, LINE_EDGE_DND_TYPE } from '../../../common/lines/lineConstants';

export const CUSTOM_TYPES = {
    IMAGE_LINK: '_image_link_',
};

export const NATIVE_TYPES = [NativeTypes.FILE, NativeTypes.URL, NativeTypes.TEXT];

export const GROUPED_DND_TYPES = {
    ELEMENT: 'element',
    LINE: 'line',
    NATIVE: 'native',
};

/**
 * This helper function determines if a child component has already handled the drop event.
 * NOTE: The child component MUST add a 'handledBy' property to the drop result in order for this to work.  The child
 * must also be a DOM child of the element in order for this to work.
 *
 * @param monitor {DropTargetMonitor} The React DnD drop target monitor.
 * @param component {ReactComponent} The React component that intends to handle the drop.
 * @returns {boolean} True if a child component has already handled the drop.
 */
export const dropTargetAlreadyHandled = (monitor, component) => {
    const domNode = ReactDOM.findDOMNode(component);
    const lastDropResult = monitor.getDropResult();
    return lastDropResult && lastDropResult.handledBy && domNode.contains(lastDropResult.handledBy);
};

// Item predicates
const sourceIsIconViewInList = ({ item }) => item.fromList && isIconViewLike(item.element);

// Drag offsets
export const getIconViewIconCentreOffset = ({ gridSize }) =>
    pointLib.getPoint((DEFAULT_ICON_VIEW_WIDTH * gridSize) / 2, (DEFAULT_ICON_VIEW_WIDTH * gridSize) / 2);

const getIconViewInListDragOrigin = getIconViewIconCentreOffset;

export const getDefaultDragOrigin = ({ initialSourceClientOffset, initialClientOffset }) =>
    pointLib.difference(initialSourceClientOffset, initialClientOffset);

export const getElementDragOrigin = cond([
    [sourceIsIconViewInList, getIconViewInListDragOrigin],
    [stubTrue, getDefaultDragOrigin],
]);

/**
 * The position from the top left of the dragged item to the actual grab position of the dragged item.
 * I expect this to be positive.
 */
export const getScaledGrabOffset = ({ clientOffset, sourceClientOffset }) =>
    pointLib.difference(sourceClientOffset, clientOffset);

// text dropping functionality
export const handleTextDrop =
    (getLocation) => (props, monitor, component, acceptedElementIds, transactionId, domNode) => {
        const { isEditable, createShortcutCard, currentBoardId } = props;

        if (!isEditable) return;

        const { text } = monitor.getItem();
        const location = getLocation(props, monitor, component);
        createShortcutCard({ location, text, currentBoardId });

        const handledBy = domNode || ReactDOM.findDOMNode(component);

        return {
            handledBy,
        };
    };

// file dropping functionality
export const handleFileDrop =
    (getLocation) => (props, monitor, component, acceptedElementIds, transactionId, domNode) => {
        const { isEditable, createShortcutFile, currentBoardId } = props;

        if (!isEditable) return;

        const { files } = monitor.getItem();
        files.forEach((file, i) => {
            const location = getLocation(props, monitor, component, i);

            createShortcutFile({
                location,
                file,
                currentBoardId,
                creationSource: ELEMENT_CREATION_SOURCES.DROPPED_ONTO_WINDOW,
            }).catch((err) => (err.type === FileValidationError.type ? null : Promise.reject(err)));
        });

        const handledBy = domNode || ReactDOM.findDOMNode(component);

        return {
            handledBy,
        };
    };

// url dropping functionality
export const handleUrlDrop =
    (getLocation) => (props, monitor, component, acceptedElementIds, transactionId, domNode) => {
        const { isEditable, createShortcutLink, currentBoardId, deselectAll } = props;
        if (!isEditable) return;
        const { urls } = monitor.getItem();

        urls.forEach((url, i) => {
            const location = getLocation(props, monitor, component, i);

            createShortcutLink({
                location,
                url,
                currentBoardId,
                creationSource: ELEMENT_CREATION_SOURCES.DROPPED_ONTO_WINDOW,
            })
                // Force the link to attempt resolution
                .then(deselectAll);
        });

        const handledBy = domNode || ReactDOM.findDOMNode(component);

        return {
            handledBy,
        };
    };

export const handleImageUrlDrop =
    (getLocation) => (props, monitor, component, acceptedElementIds, transactionId, domNode) => {
        const { isEditable, createShortcutImageLink, currentBoardId } = props;
        if (!isEditable) return;
        const { url, src, width, height, isDataUri } = monitor.getItem();

        const location = getLocation(props, monitor, component);

        createShortcutImageLink({ location, currentBoardId, src, url, width, height, isDataUri });

        const handledBy = domNode || ReactDOM.findDOMNode(component);

        return {
            handledBy,
        };
    };

// Object returned by this function will become the dropResult.
export const handleElementDrop =
    (getLocation, getDropState) => (props, monitor, component, acceptedElementIds, transactionId, domNode) => {
        const { getContextZoomScale = () => 1 } = props;

        const handledBy = domNode || ReactDOM.findDOMNode(component);
        const location = getLocation(props, monitor, component);
        const dropState = getDropState ? getDropState(props, monitor, component) : undefined;

        // The Milanote element (Card, Board, Column, etc.) that's accepting the drop
        const dropTargetElement =
            props?.element ||
            // For the canvas:
            props?.currentBoard;

        const zoomScaleOnDropTarget = getContextZoomScale();

        return {
            location,
            dropState,
            handledBy,
            dropTargetElement,
            acceptedElementIds,
            transactionId,
            zoomScaleOnDropTarget,
            clientOffset: monitor?.getClientOffset(),
        };
    };

export const handleTaskDrop = (getTaskListLocation) => (props, monitor, component) => {
    const { createTaskList, dispatchRevealElement } = props;
    const { draggedElementIds } = monitor.getItem();

    const domNode = ReactDOM.findDOMNode(component);

    const dropLocation = getTaskListLocation(props, monitor, component);

    const { taskListElementId, transactionId } = createTaskList({
        location: dropLocation,
        content: {
            [SHOW_TITLE_PROPERTY]: false,
        },
        // Hide until the move finishes to prevent the flash of empty task list
        // (there will instead be a flash of nothing)
        temp: {
            hidden: true,
        },
    });

    // Reveal the task list after the move has completed
    defer(() => dispatchRevealElement({ elementId: taskListElementId }));

    const getTaskListChildLocation = () => getListChildNewLocation({ listId: taskListElementId });

    return handleElementDrop(getTaskListChildLocation)(
        props,
        monitor,
        component,
        draggedElementIds,
        transactionId,
        domNode,
    );
};

/**
 * This handles when task lists are dropped onto tasks / task lists.
 * We need to first delete the original task lists, then move the child tasks to the drop target task / task list.
 * This will perform the deleting of the tasks, the moving of the task lists will be handled in the
 * DraggableElement.
 */
export const handleTaskListDropOnTasks = (getLocation) => (props, monitor, component) => {
    const { dispatchMoveElementsToTrash, currentBoardId } = props;
    const { draggedElementIds, draggedElements } = monitor.getItem();

    // Delete the original task lists
    const transactionId = dispatchMoveElementsToTrash({
        elements: Immutable.List(draggedElements),
        currentBoardId,
    });

    // Return the location that the child tasks should be moved to
    return handleElementDrop(getLocation)(props, monitor, component, draggedElementIds, transactionId);
};

export const getGroupedItemType = (itemType) => {
    switch (itemType) {
        case ELEMENT_DND_TYPE:
            return GROUPED_DND_TYPES.ELEMENT;
        case LINE_EDGE_DND_TYPE:
        case LINE_CONTROL_POINT_DND_TYPE:
            return GROUPED_DND_TYPES.LINE;
        case NativeTypes.FILE:
        case NativeTypes.TEXT:
        case NativeTypes.URL:
            return GROUPED_DND_TYPES.NATIVE;
        default:
            return null;
    }
};
